import { runTransaction, serverTimestamp } from 'firebase/firestore';
import { omit } from 'ramda';
import { Countries, Weddings } from '@bridebook/models';
import { getFirestore } from '@bridebook/models/source/firebase/firestore';
import type { ITask } from '@bridebook/models/source/models/Countries/Tasks.types';
import type { ITask as IWeddingTask } from '@bridebook/models/source/models/Weddings/Tasks.types';
import type { IWedding } from '@bridebook/models/source/models/Weddings.types';
import gazetteer, { type CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import type { IDatePicker } from '@bridebook/toolbox/src/datepicker/types';
import type { WithFieldValue } from '@firebase/firestore';
import { getSplitTasks } from './Tasks.utils';
import { getTasksExactDate } from './utils';

export type InitialiseChecklistResult = {
  /** False when nothing was written: no tasks, or already initialised. */
  persisted: boolean;
  taskCount: number;
  /** Why nothing was written, when nothing was. */
  reason?: 'noTasks' | 'alreadyInitialised';
};

/**
 * Builds and persists the couple's checklist, and answers whether it landed. One async core
 * for the date picker's epic and the coach: two writers would drift, and the coach needs a
 * promise to await. Idempotent inside the transaction, which is the only guard that covers
 * the coach racing the date picker, a second tab, or itself.
 */
export const initialiseChecklistTasks = async ({
  weddingId,
  date,
  country,
  initializedAt,
  roles,
  isSameSexRoles,
  initial,
}: {
  weddingId: string;
  date: IDatePicker;
  country: CountryCodes;
  initializedAt?: number;
  roles?: IWedding['roles'];
  isSameSexRoles: boolean;
  initial: boolean;
}): Promise<InitialiseChecklistResult> => {
  const exactDate = getTasksExactDate(date, gazetteer.getMarketByCountry(country));

  const wedding = Weddings._.getById(weddingId);
  const weddingTasksRef = wedding.Tasks;
  const existingTasks = (await weddingTasksRef.query().get()) as Record<string, IWeddingTask>;

  let tasks: ITask[];
  if (initial) {
    const countriesTasks = await Countries._.getById(country).getActiveTasks();
    // Roles on a first initialisation only: they split the role tasks between partners.
    tasks = await getSplitTasks(countriesTasks, exactDate, initializedAt, roles, isSameSexRoles);
  } else {
    tasks = await getSplitTasks(
      existingTasks as unknown as Record<string, ITask>,
      exactDate,
      initializedAt,
    );
  }

  // Never `initializedAt` without tasks: the page renders empty with no way back.
  if (tasks.length === 0) {
    return { persisted: false, taskCount: 0, reason: 'noTasks' };
  }

  let aborted = false;
  await runTransaction(getFirestore(), async (transaction) => {
    aborted = false;
    const weddingDoc = await transaction.get(wedding.reference);
    const liveInitializedAt = weddingDoc.get('tasks.initializedAt');
    // The live read decides, not the caller's copy: a first initialisation over an
    // existing checklist gives every outstanding task a fresh `createdAt`.
    if (initial && liveInitializedAt) {
      aborted = true;
      return;
    }

    for (const task of tasks) {
      // A re-initialisation only moves tasks in time. The tasks were read outside the
      // transaction, so writing back anything else could undo ticks or renames made meanwhile.
      if (!initial) {
        transaction.set(
          weddingTasksRef.getById(task.id).reference,
          { period: task.period, order: task.order, updatedAt: serverTimestamp() },
          { merge: true },
        );
        continue;
      }

      const existingTask = existingTasks[task.id];
      const taskData: WithFieldValue<ITask> = omit(['active', 'i18nKey', 'key'], {
        ...existingTask,
        custom: existingTask?.custom ?? false,
        done: existingTask?.done ?? false,
        ...task,
        ...(!existingTask?.done && { createdAt: serverTimestamp() }),
        updatedAt: serverTimestamp(),
      });
      transaction.set(weddingTasksRef.getById(task.id).reference, taskData, { merge: true });
    }

    if (!liveInitializedAt) {
      transaction.update(wedding.reference, 'tasks', { initializedAt: serverTimestamp() });
    }
  });

  if (aborted) {
    return { persisted: false, taskCount: 0, reason: 'alreadyInitialised' };
  }
  return { persisted: true, taskCount: tasks.length };
};
